<template>
  <div class="viewport viewport-with-top-nav viewport-compact">

    <TopNav></TopNav>
    <div class="display-1 my-3">{{ $t('UpdateAccountPage.title') }}</div>

    <div>
      <validation-observer ref="form">
        <div class="form-group">
          <validation-provider vid="phoneNumber" rules="required|numeric" v-slot="{ errors }">
            <label for="userPhone">{{ $t('UpdateAccountPage.phone') }}</label>
            <input type="text" v-model.trim="phoneNumber" placeholder="012345678" id="userPhone"
                   class="form-control form-control-lg" :class="{'is-invalid': errors.length}" inputmode="tel">
            <span class="text-muted">{{ $t('UpdateAccountPage.phoneHint') }}</span>
            <span class="invalid-feedback">{{ errors[0] }}</span>
          </validation-provider>
        </div>
        <div>
          <button id="update-account-submit" class="btn btn-lg btn-primary btn-block" @click="onSubmitClicked()">
            {{ $t('UpdateAccountPage.saveChanges') }}
          </button>
        </div>
      </validation-observer>
    </div>

    <div class="flex-grow-1"></div>
  </div>

</template>

<script>
import TopNav from '@shared/components/TopNav'
import store from '@shared/store'
import settings from '@shared/settings'
import GetIDMixin from '@shared/mixins/GetIDMixin'

export default {
  name: 'UpdateAccountPage',
  components: { TopNav },
  mixins: [GetIDMixin],
  data() {
    return {
      phoneNumber: undefined,
      isDesktop: settings.isDesktopWeb(),
      isPkiSigningEnabled: settings.isPkiSigningSupported() && settings.featureFlags.isPkiSigningEnabled(),
      isIdentityVerificationEnabled: settings.isGetIdSupported() && settings.featureFlags.isIdentityVerificationEnabled()
    }
  },
  created() {
    if (!settings.featureFlags.isPhoneNumberChangeEnabled() || settings.isDesktopWeb()) {
      this.$router.replace({ name: 'customer-overview' })
    }

    if (this.isPkiSigningEnabled) {
      this.$root.$on('certificate-exists', this.onCertificateExists)
      this.$root.$on('csr-generated', this.onCsrGenerated)
      this.$root.$on('certificate-stored', this.onCertificateStored)
      this.$root.$on('signed', this.onSigned)
    } else if (this.isIdentityVerificationEnabled) {
      this.$root.$on('verification-completed', this.onVerificationComplete)
    }
  },
  destroyed() {
    if (this.isPkiSigningEnabled) {
      this.$root.$off('certificate-exists', this.onCertificateExists)
      this.$root.$off('csr-generated', this.onCsrGenerated)
      this.$root.$off('certificate-stored', this.onCertificateStored)
      this.$root.$off('signed', this.onSigned)
    } else if (this.isIdentityVerificationEnabled) {
      this.$root.$off('verification-completed', this.onVerificationComplete)
    }
  },
  methods: {
    async onSubmitClicked() {
      const success = await this.$refs.form.validate()
      if (success) {
        this.saveAccountChanges()
      }
    },
    saveAccountChanges() {
      if (this.isDesktop) return

      if (this.isPkiSigningEnabled) {
        const parameters = JSON.stringify(this.customerParameters(this.biometryLabels('updateAccount')))
        if (settings.isAndroid()) {
          window.publicKeyInfrastructure.initializeSigning(parameters)
        } else if (settings.isIOS()) {
          window.webkit.messageHandlers.pkiInitializeSigning.postMessage(parameters)
        }
      } else if (this.isIdentityVerificationEnabled) {
        this.startGetID({ flowName: 'PhoneNumberUpdate', phoneNumber: this.phoneNumber })
      }
    },
    async onCsrGenerated(csr) {
      const customerData = store.state.currentUser
      const response = await this.$http.post(`/api/pki/${customerData.pin}/issue-certificate`, csr)
      const certificate = response.data.certificate
      const parameters = JSON.stringify({ ...this.customerParameters(), certificate })
      if (settings.isAndroid()) {
        window.publicKeyInfrastructure.storeIssuedCertificate(parameters)
      } else if (settings.isIOS()) {
        window.webkit.messageHandlers.pkiStoreIssuedCertificate.postMessage(parameters)
      }
    },
    onCertificateStored() {
      this.confirmUpdateAccount()
    },
    onCertificateExists() {
      this.confirmUpdateAccount()
    },
    confirmUpdateAccount() {
      this.digest = btoa(JSON.stringify({ phone: this.phoneNumber }))
      const parameters = JSON.stringify({
        ...this.customerParameters(this.biometryLabels('updateAccount')),
        dataToSign: this.digest
      })

      if (settings.isAndroid()) {
        window.publicKeyInfrastructure.sign(parameters)
      } else if (settings.isIOS()) {
        window.webkit.messageHandlers.pkiSign.postMessage(parameters)
      }
    },
    async onSigned(payload) {
      const customerData = store.state.currentUser
      await this.$http.post(`/api/customers/${customerData.pin}/update-account/biometry`, {
        digest: this.digest,
        signature: payload.signature // signature is a Base64 string already
      })
      this.$successToast(this.$t('UpdateAccountPage.accountUpdated'))
      await this.$router.push({ name: 'account' })
    },
    customerParameters(labels = {}) {
      const customerData = store.state.currentUser
      return {
        firstName: customerData.firstName,
        lastName: customerData.lastName,
        pin: customerData.pin,
        birthDate: customerData.birthDate,
        labels: labels
      }
    },
    biometryLabels(prefix) {
      return {
        title: this.$t(`biometry.${prefix}.title`),
        subtitle: this.$t(`biometry.${prefix}.subtitle`),
        description: this.$t(`biometry.${prefix}.description`),
        cancel: this.$t(`biometry.${prefix}.cancel`)
      }
    },
    onVerificationComplete({ verificationId }) {
      this.$http.post(`/api/customers/${store.state.currentUser.pin}/update-account/getId`, {
        verificationId,
        phoneNumber: this.phoneNumber
      })
    }
  }
}
</script>
